// Core Modules
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

// External Modules
import { catchError, Observable, of, tap } from 'rxjs';

// Services
import { CacheService } from '@core/services/cache/cache.service';
import { ErrorHandlerService } from '@core/services/error-handler/error-handler.service';

/**
 * Http Adapter Service
 */
@Injectable({
  providedIn: 'root',
})
export class HttpAdapterService {
  // Injectors
  private readonly cacheService = inject(CacheService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly httpClient = inject(HttpClient);

  /**
   * Constructs a DELETE request that interprets the body as a JSON object
   * and returns an observable of the response
   * @param url API url
   * @param body Request criteria
   * @returns An Observable of the HttpResponse
   */
  public delete<T>(url: string, body: Record<string, any>): Observable<T> {
    return this.httpClient.delete<T>(url, body).pipe(
      tap((result) => result),
      catchError((err: HttpErrorResponse) => {
        return this.errorHandlerService.errorHandler(err);
      })
    );
  }

  /**
   * Constructs a GET request that interprets the body as a JSON object
   * and returns the response body
   * @param url API url
   * @param cacheKey It is the combination of request criteria and clientId,
   * if it is passed then this method will set and get the data to and from the cache
   * @returns An Observable of the HttpResponse
   */
  public get<T>(url: string, options?: Record<string, any>, cacheKey?: string): Observable<T> {
    if (cacheKey && this.cacheService.isCacheExists(cacheKey)) {
      const cachedResp = this.cacheService.getCacheData(cacheKey);
      cachedResp.cacheKey = cacheKey;
      return of(cachedResp);
    }

    return this.httpClient.get<T>(url, options).pipe(
      tap((result) => {
        if (cacheKey) {
          if (this.cacheService.isCacheExists(cacheKey)) return result;
          this.cacheService.createCache(cacheKey).put(cacheKey, result);
        }
        return result;
      }),
      catchError((err: HttpErrorResponse) => {
        return this.errorHandlerService.errorHandler(err);
      })
    );
  }

  /**
   * Constructs a POST request that interprets the body as a JSON object
   * and returns an observable of the response
   * @param url API url
   * @param body Request criteria
   * @returns An Observable of the HttpResponse
   */
  public post<T>(url: string, body: Record<string, any> | Record<string, any>[], options?: Record<string, any>): Observable<T> {
    return this.httpClient.post<T>(url, body, options).pipe(
      tap((result) => result),
      catchError((err: HttpErrorResponse) => {
        return this.errorHandlerService.errorHandler(err);
      })
    );
  }

  /**
   * Constructs a PUT request that interprets the body as a JSON object
   * and returns an observable of the response
   * @param url API url
   * @param body Request criteria
   * @returns An Observable of the HttpResponse
   */
  public put<T>(url: string, body: Record<string, any> | Record<string, any>[], options?: Record<string, any>): Observable<T> {
    return this.httpClient.put<T>(url, body, options).pipe(
      tap((result) => result),
      catchError((err: HttpErrorResponse) => {
        return this.errorHandlerService.errorHandler(err);
      })
    );
  }

  /**
   * Constructs a PATCH request that interprets the body as a JSON object
   * and returns an observable of the response
   * @param url API url
   * @param body Request criteria
   * @returns An Observable of the HttpResponse
   */
  public patch<T>(url: string, body: Record<string, any> | Record<string, any>[]): Observable<T> {
    return this.httpClient.patch<T>(url, body).pipe(
      tap((result) => result),
      catchError((err: HttpErrorResponse) => {
        return this.errorHandlerService.errorHandler(err);
      })
    );
  }
}
